import { useState, useEffect } from "react";
import { getSmtpConfigs, createSmtpConfig, updateSmtpConfig, deleteSmtpConfig, testSmtpConfig, saveSmtpConfigMultipart, deleteSmtpStamp } from "../../mailerApi";
import "./Mailer.css";

const PRESETS = {
  gmail: { host: "smtp.gmail.com", port: 587, note: "Gmail — საჭიროა App Password" },
  outlook: { host: "smtp.office365.com", port: 587, note: "Outlook / Office 365" },
  yahoo: { host: "smtp.mail.yahoo.com", port: 587, note: "Yahoo Mail" },
  custom: { host: "", port: 587, note: "სხვა SMTP სერვერი" },
};

const EMPTY_FORM = {
  host: "smtp.gmail.com",
  port: 587,
  username: "",
  password: "",
  use_ssl: true,
  display_name: "",
};

export default function SmtpSettingsPage() {
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [preset, setPreset] = useState("gmail");
  const [editing, setEditing] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [hasPassword, setHasPassword] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [stampFile, setStampFile] = useState(null);
  const [stampPreview, setStampPreview] = useState(null);
  const [existingStampUrl, setExistingStampUrl] = useState(null);

  async function loadConfigs() {
    try {
      const data = await getSmtpConfigs();
      const list = Array.isArray(data) ? data : data.results || [];
      setConfigs(list);
    } catch (e) {
      // no configs
    }
  }

  useEffect(() => { loadConfigs(); }, []);

  function handlePreset(key) {
    setPreset(key);
    const p = PRESETS[key];
    setForm(prev => ({ ...prev, host: p.host, port: p.port }));
  }

  function handleAddNew() {
    setEditing(null);
    setHasPassword(false);
    setForm({ ...EMPTY_FORM });
    setPreset("gmail");
    setShowForm(true);
    setTestResult(null);
    setError("");
    setStampFile(null);
    setStampPreview(null);
    setExistingStampUrl(null);
  }

  function handleEdit(cfg) {
    setEditing(cfg.id);
    setHasPassword(cfg.password_set);
    setForm({
      host: cfg.host || "smtp.gmail.com",
      port: cfg.port || 587,
      username: cfg.username || "",
      password: "",
      use_ssl: cfg.use_ssl !== false,
      display_name: cfg.display_name || "",
    });
    const found = Object.entries(PRESETS).find(([, v]) => v.host === cfg.host);
    setPreset(found ? found[0] : "custom");
    setShowForm(true);
    setTestResult(null);
    setError("");
    setStampFile(null);
    setStampPreview(null);
    setExistingStampUrl(cfg.stamp_image_url || null);
  }

  function handleStampChange(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("ფაილი უნდა იყოს სურათი (PNG/JPG)");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError("სურათი 5MB-ზე ნაკლები უნდა იყოს");
      return;
    }
    setError("");
    setStampFile(f);
    const reader = new FileReader();
    reader.onload = () => setStampPreview(reader.result);
    reader.readAsDataURL(f);
  }

  async function handleRemoveStamp() {
    if (!editing) {
      setStampFile(null);
      setStampPreview(null);
      return;
    }
    if (!window.confirm("წავშალოთ ტიტულარი ამ ანგარიშისთვის?")) return;
    try {
      await deleteSmtpStamp(editing);
      setStampFile(null);
      setStampPreview(null);
      setExistingStampUrl(null);
      loadConfigs();
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("წაიშალოს ეს ანგარიში?")) return;
    try {
      await deleteSmtpConfig(id);
      loadConfigs();
      if (editing === id) {
        setEditing(null);
        setShowForm(false);
      }
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleSave() {
    setError("");
    setSaved(false);
    try {
      const fields = {
        host: form.host,
        port: form.port,
        username: form.username,
        use_ssl: form.use_ssl,
        display_name: form.display_name,
      };
      if (form.password) fields.password = form.password;
      if (!editing && !form.password) {
        setError("პაროლი სავალდებულოა");
        return;
      }

      // If a stamp file is selected, use multipart endpoint; otherwise plain JSON.
      if (stampFile) {
        await saveSmtpConfigMultipart(editing, fields, stampFile);
      } else if (editing) {
        await updateSmtpConfig(editing, fields);
      } else {
        await createSmtpConfig(fields);
      }
      setSaved(true);
      setShowForm(false);
      loadConfigs();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      await testSmtpConfig({
        id: editing || undefined,
        host: form.host,
        port: form.port,
        username: form.username,
        password: form.password,
      });
      setTestResult({ success: true, message: "კავშირი წარმატებულია!" });
    } catch (e) {
      setTestResult({ success: false, message: e.message });
    }
    setTesting(false);
  }

  return (
    <div className="mailer-page">
      <div className="mailer-header">
        <h1>SMTP პარამეტრები</h1>
      </div>

      {error && <div className="mailer-error">{error}<button onClick={() => setError("")}>×</button></div>}
      {saved && <div className="mailer-success">პარამეტრები შენახულია!</div>}

      <div className="settings-layout">
        <div className="settings-form-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0 }}>გამგზავნი ანგარიშები</h3>
            <button className="btn btn-primary btn-sm" onClick={handleAddNew}>+ ანგარიშის დამატება</button>
          </div>

          {configs.length === 0 && !showForm && (
            <p style={{ color: "#64748b", textAlign: "center", padding: 24 }}>
              ანგარიშები არ არის დამატებული. დააჭირეთ "+ ანგარიშის დამატება".
            </p>
          )}

          <div className="smtp-accounts-list">
            {configs.map(cfg => (
              <div key={cfg.id} className={`smtp-account-card ${cfg.is_active ? "active" : ""} ${editing === cfg.id && showForm ? "selected" : ""}`}>
                <div className="smtp-account-info">
                  <span className="smtp-account-name">{cfg.display_name || "უსახელო"}</span>
                  <span className="smtp-account-email">{cfg.username}</span>
                  <span className="smtp-account-host">{cfg.host}:{cfg.port}</span>
                </div>
                <div className="smtp-account-actions">
                  {cfg.is_active && <span className="smtp-badge active">აქტიური</span>}
                  <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(cfg)}>რედაქტირება</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(cfg.id)}>წაშლა</button>
                </div>
              </div>
            ))}
          </div>

          {showForm && (
            <div className="smtp-form-section">
              <h4 style={{ marginBottom: 12 }}>{editing ? "ანგარიშის რედაქტირება" : "ახალი ანგარიშის დამატება"}</h4>

              <div className="preset-buttons">
                {Object.entries(PRESETS).map(([key]) => (
                  <button
                    key={key}
                    className={`preset-btn ${preset === key ? "active" : ""}`}
                    onClick={() => handlePreset(key)}
                  >
                    {key === "gmail" ? "Gmail" : key === "outlook" ? "Outlook" : key === "yahoo" ? "Yahoo" : "სხვა"}
                  </button>
                ))}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>SMTP Host</label>
                  <input value={form.host} onChange={e => setForm({...form, host: e.target.value})} placeholder="smtp.gmail.com" />
                </div>
                <div className="form-group" style={{maxWidth: 120}}>
                  <label>Port</label>
                  <input type="number" value={form.port} onChange={e => setForm({...form, port: Number(e.target.value)})} />
                </div>
              </div>

              <div className="form-group">
                <label>ელ. ფოსტა (username)</label>
                <input type="email" value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="your@gmail.com" />
              </div>

              <div className="form-group">
                <label>
                  პაროლი / App Password
                  {editing && hasPassword && !form.password && (
                    <span style={{color: "#16a34a", fontWeight: 400, marginLeft: 8}}>(შენახულია)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder={editing && hasPassword ? "ახლის ჩასაწერად შეავსეთ..." : "••••••••••••••••"}
                />
              </div>

              <div className="form-group">
                <label>გამგზავნის სახელი</label>
                <input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="კომპანიის სახელი" />
              </div>

              <div className="form-group">
                <label>
                  ტიტულარი (ხელმოწერა / ლოგო)
                  <span style={{color: "#64748b", fontWeight: 400, fontSize: 12, marginLeft: 8}}>
                    PNG/JPG, max 5MB — გამოჩნდება ყველა გაგზავნილი წერილის ბოლოში
                  </span>
                </label>
                <div className="stamp-upload-block">
                  {(stampPreview || existingStampUrl) && (
                    <div className="stamp-preview">
                      <img
                        src={stampPreview || existingStampUrl}
                        alt="ტიტულარი"
                        style={{ maxWidth: 320, maxHeight: 160, border: "1px solid #e2e8f0", borderRadius: 6, padding: 4, background: "#fff" }}
                      />
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <label className="btn btn-secondary btn-sm" style={{ cursor: "pointer" }}>
                      {(stampPreview || existingStampUrl) ? "შეცვლა" : "ატვირთვა"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleStampChange}
                        style={{ display: "none" }}
                      />
                    </label>
                    {(stampPreview || existingStampUrl) && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={handleRemoveStamp}>
                        წაშლა
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="settings-actions">
                <button className="btn btn-secondary" onClick={() => { setShowForm(false); setEditing(null); }}>
                  გაუქმება
                </button>
                <button className="btn btn-secondary" onClick={handleTest} disabled={testing || !form.username || (!form.password && !hasPassword)}>
                  {testing ? "მოწმდება..." : "კავშირის ტესტი"}
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={!form.username}>
                  შენახვა
                </button>
              </div>

              {testResult && (
                <div className={`test-result ${testResult.success ? "success" : "error"}`}>
                  {testResult.message}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="settings-help-card">
          <h3>ინსტრუქცია</h3>

          <div className="help-section">
            <h4>Gmail-ის დაყენება</h4>
            <ol>
              <li>შედით <strong>Google Account</strong>-ზე → <strong>Security</strong></li>
              <li>ჩართეთ <strong>2-Step Verification</strong> (თუ არ არის ჩართული)</li>
              <li>Security → <strong>App passwords</strong> (ძებნით მონახეთ)</li>
              <li>აირჩიეთ "Mail" და "Other" → ჩაწერეთ სახელი</li>
              <li>დააჭირეთ <strong>Generate</strong></li>
              <li>დაგენერირებული <strong>16-ნიშნა კოდი</strong> ჩასვით პაროლის ველში</li>
            </ol>
            <div className="help-note">
              <strong>მნიშვნელოვანი:</strong> ჩვეულებრივი Gmail პაროლი არ იმუშავებს! მხოლოდ App Password.
            </div>
          </div>

          <div className="help-section">
            <h4>Outlook / Office 365</h4>
            <ol>
              <li>Host: <code>smtp.office365.com</code></li>
              <li>Port: <code>587</code></li>
              <li>Username: თქვენი Outlook მეილი</li>
              <li>Password: თქვენი Outlook პაროლი</li>
            </ol>
          </div>

          <div className="help-section">
            <h4>ტესტი</h4>
            <p>შენახვამდე დააჭირეთ <strong>"კავშირის ტესტი"</strong> რომ დარწმუნდეთ პარამეტრები სწორია.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
